import TimeIcon from "app/components/Icons/TimeIcon";
import { TimeTracker } from "app/containers/Features/components/TimeTracker";
import TimerDisplay from "app/containers/Features/components/TimeTracker/TimerDisplay";
import { selectBoardForm } from "app/containers/Features/containers/Boards/components/Settings/redux/selector";
import { ticketGroupsActions } from "app/containers/Features/containers/Boards/containers/Tickets/redux/slice";
import { ITickets } from "app/containers/Features/containers/Boards/containers/Tickets/types";
import { selectActiveTimerForm } from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { selectUserFormData } from "app/redux/selector";
import { BoardViewType } from "app/types";
import clsx from "clsx";
import React, { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTimeTracker } from "utils/hooks/useTimeTracker";

interface CustomProps {
  ticketDetail: ITickets;
}

const EffortActual: FC<CustomProps> = ({ ticketDetail }) => {
  // Hooks
  const dispatch = useDispatch();

  // Global States
  const currentUser = useSelector(selectUserFormData);
  const boardForm = useSelector(selectBoardForm);
  const activeTimerForm = useSelector(selectActiveTimerForm);
  const { isRunning } = useTimeTracker();

  // Local States
  const [menuVisibility, setMenuVisibility] = useState<boolean>(false);

  // Variables
  const timerRunning =
    isRunning &&
    activeTimerForm?.selectedTicketDetails._id === ticketDetail?._id;
  const kanbanView = BoardViewType.KANBAN === boardForm.viewType;
  const listView = BoardViewType.LIST === boardForm.viewType;
  const currentEffortActual = useMemo(
    () =>
      ticketDetail.effortActual?.find(
        (effort) => effort.memberId === ticketDetail.assignedTo,
      ),
    [ticketDetail.effortActual, ticketDetail.assignedTo],
  );
  const formattedEffort =
    currentEffortActual?.effort?.hours || currentEffortActual?.effort?.minutes
      ? `${currentEffortActual.effort.hours}:${currentEffortActual.effort.minutes}`
      : "-";

  const FormattedEffort = () => {
    return (
      <div className={clsx("flex items-center gap-1 w-full min-w-0 overflow-hidden")}>
        <span className="flex-shrink-0">
          <TimeIcon size={18} />
        </span>
        <span className="text-sm font-normal text-secondary truncate min-w-0">
          {formattedEffort}
        </span>
      </div>
    );
  };

  return (
    <TimeTracker
      tooltipText="Efforts actual"
      menuVisibility={menuVisibility}
      onOpenChange={(open) => setMenuVisibility(open)}
      assigneeId={ticketDetail?.assignedTo}
      toolTipPosition="left"
      portalProps={{
        contentProps: {
          align: "end",
        },
        totalEffortActual: ticketDetail?.totalEffortActual,
        effortActual: ticketDetail?.effortActual,
        selectedTicketDetails: {
          _id: ticketDetail?._id,
          projectId: currentUser?.selectedProjectId,
          ticketName: ticketDetail?.ticketName,
          typeIcon: ticketDetail?.type,
        },
        callback: ({ form, response }) => {
          dispatch(
            ticketGroupsActions.updateTicketFieldLocally({
              ticketIds: form.selectedTicketDetails._id,
              updates: {
                ...ticketDetail,
                effortActual: [...ticketDetail?.effortActual, response],
              },
            }),
          );
        },
      }}
      triggerClassName={clsx(
        "!justify-start min-w-0 w-full overflow-hidden p-1.5 hover:!bg-gray-400 !rounded-md",
        kanbanView && timerRunning && "!p-0 hover:!bg-white",
        listView && timerRunning && "!p-0 !border-none",
      )}
    >
      {timerRunning ? (
        <TimerDisplay
          iconSize={17}
          className={clsx(
            "text-sm",
            menuVisibility && !kanbanView && "border !border-secondary",
            kanbanView && "!border-none",
          )}
        />
      ) : (
        <FormattedEffort />
      )}
    </TimeTracker>
  );
};

export default EffortActual;
